import React3, { forwardRef, useRef, useState, useEffect, useImperativeHandle, useMemo, useCallback } from 'react';

// @smarterservices/smarter-elements v1.0.24
var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/utils/jwt.ts
function createJWT(payload) {
  const header = {
    alg: "none",
    typ: "JWT"
  };
  try {
    const headerJson = JSON.stringify(header);
    const payloadJson = JSON.stringify(payload);
    const encodedHeader = btoa(encodeURIComponent(headerJson).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)))).replace(/=+$/, "");
    const encodedPayload = btoa(encodeURIComponent(payloadJson).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)))).replace(/=+$/, "");
    return `${encodedHeader}.${encodedPayload}.`;
  } catch (error) {
    console.error("JWT encoding failed, using simple fallback:", error);
    const encodedHeader = btoa(JSON.stringify(header)).replace(/=+$/, "");
    const encodedPayload = btoa(JSON.stringify(payload)).replace(/=+$/, "");
    return `${encodedHeader}.${encodedPayload}.`;
  }
}
__name(createJWT, "createJWT");
function decodeJWT(token) {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }
    const payload = parts[1];
    try {
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      const decodedJson = decodeURIComponent(decoded.split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join(""));
      return JSON.parse(decodedJson);
    } catch (unicodeError) {
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    }
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}
__name(decodeJWT, "decodeJWT");
function hasProperty(obj, prop) {
  return prop in obj;
}
__name(hasProperty, "hasProperty");
function getJWTValue(token, key) {
  const payload = decodeJWT(token);
  return payload && hasProperty(payload, key) ? payload[key] : void 0;
}
__name(getJWTValue, "getJWTValue");

// ../../libs/shared/src/config/default.ts
var defaultConfig = {
  proctoring: {
    api: {
      baseUrl: "http://localhost:8011",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "http://localhost:4000",
      secondCamera: {
        deviceBaseUrl: "http://localhost:4000",
        shortDeviceBaseUrl: "http://localhost:4000"
      },
      openReplay: {
        projectKey: "kryHYdOJ31O0FeYIOvZ5",
        disableSecureMode: true
      }
    }
  },
  assessment: {
    api: {
      baseUrl: "http://localhost:8010",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "http://localhost:8013",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "http://localhost:8012",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "http://localhost:8012"
    },
    livekit: {
      websocketUrl: "wss://media-dev-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "http://localhost:4000"
    }
  },
  logging: {
    logLevel: "debug",
    logDestination: "both",
    betterStack: {
      token: "ZeYUq78zmvf3wDnxrqDVAMBo",
      host: "s1387105.us-east-9.betterstackdata.com"
    }
  }
};

// ../../libs/shared/src/config/development.ts
var developmentConfig = {
  proctoring: {
    api: {
      baseUrl: "https://api.dev.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "https://app.dev.smarterservices.com",
      secondCamera: {
        deviceBaseUrl: "https://app.dev.smarterservices.com",
        shortDeviceBaseUrl: "https://camera.dev.smarterservices.com"
      }
    }
  },
  assessment: {
    api: {
      baseUrl: "",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "https://signon.dev.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "https://playback.dev.proctoring.media",
      timeout: 3e4,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "https://playback.dev.proctoring.media"
    },
    livekit: {
      websocketUrl: "wss://media-dev-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "https://app.dev.smarterservices.com"
    }
  },
  logging: {
    logLevel: "warn",
    logDestination: "remote"
  }
};

// ../../libs/shared/src/config/stage.ts
var stageConfig = {
  proctoring: {
    api: {
      baseUrl: "https://api.stage.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "https://app.stage.smarterservices.com"
    }
  },
  assessment: {
    api: {
      baseUrl: "",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "https://signon.stage.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "https://playback.stage.proctoring.media",
      timeout: 3e4,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "https://playback.stage.proctoring.media"
    },
    livekit: {
      websocketUrl: "wss://media-stage-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "https://app.stage.smarterservices.com"
    }
  },
  logging: {
    logLevel: "warn",
    logDestination: "remote"
  }
};

// ../../libs/shared/src/config/production.ts
var productionConfig = {
  proctoring: {
    api: {
      baseUrl: "https://api.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "https://app.smarterservices.com",
      secondCamera: {
        deviceBaseUrl: "https://app.smarterservices.com",
        shortDeviceBaseUrl: "https://camera.smarterproctoring.com"
      }
    }
  },
  assessment: {
    api: {
      baseUrl: "",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "https://signon.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "https://playback.proctoring.media",
      timeout: 3e4,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "https://playback.proctoring.media"
    },
    livekit: {
      websocketUrl: "wss://media-prd-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "https://app.smarterservices.com"
    }
  },
  logging: {
    logLevel: "error",
    logDestination: "remote"
  }
};

// ../../libs/shared/src/config/subenvironment.ts
var subEnvironmentConfig = {
  proctoring: {
    api: {
      baseUrl: "https://api.sub.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "https://app.sub.smarterservices.com"
    }
  },
  assessment: {
    api: {
      baseUrl: "",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "https://signon.sub.smarterproctoring.com",
      timeout: 3e4,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "https://playback.sub.proctoring.media",
      timeout: 3e4,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "https://playback.sub.proctoring.media"
    },
    livekit: {
      websocketUrl: "wss://media-dev-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "https://app.sub.smarterservices.com"
    }
  },
  logging: {
    logLevel: "error",
    logDestination: "remote"
  }
};

// ../../libs/shared/src/config/local.ts
var localConfig = {
  proctoring: {
    api: {
      baseUrl: "http://localhost:8011",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    },
    ui: {
      baseUrl: "http://localhost:4000",
      secondCamera: {
        deviceBaseUrl: "https://jasonfill.ngrok.io"
      }
    }
  },
  assessment: {
    api: {
      baseUrl: "http://localhost:8010",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    }
  },
  core: {
    api: {
      baseUrl: "http://localhost:8013",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    }
  },
  media: {
    api: {
      baseUrl: "http://localhost:8012",
      timeout: 3e4,
      retry: true,
      retryAttempts: 3
    },
    playback: {
      baseUrl: "http://localhost:8012"
    },
    livekit: {
      websocketUrl: "wss://media-dev-nyc1.proctoring.media"
    }
  },
  elements: {
    ui: {
      baseUrl: "http://localhost:4000"
    }
  }
};

// ../../libs/shared/src/config/index.ts
function deepMerge(target, ...sources) {
  const result = JSON.parse(JSON.stringify(target));
  if (!sources.length) return result;
  for (const source of sources) {
    if (!source) continue;
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const sourceValue = source[key];
        const resultValue = result[key];
        if (sourceValue === null || sourceValue === void 0) {
          continue;
        } else if (Array.isArray(sourceValue)) {
          result[key] = [
            ...sourceValue
          ];
        } else if (typeof sourceValue === "object" && resultValue && typeof resultValue === "object" && !Array.isArray(resultValue)) {
          result[key] = deepMerge(resultValue, sourceValue);
        } else {
          result[key] = sourceValue;
        }
      }
    }
  }
  return result;
}
__name(deepMerge, "deepMerge");
function getEnvironmentConfig(env = "local") {
  switch (env) {
    case "production":
      return productionConfig;
    case "development":
      return developmentConfig;
    case "staging":
      return stageConfig;
    case "subenvironment":
      return subEnvironmentConfig;
    case "local":
      return localConfig;
    default:
      return defaultConfig;
  }
}
__name(getEnvironmentConfig, "getEnvironmentConfig");
function buildConfig(env = "development") {
  const baseConfig = deepMerge({}, defaultConfig);
  const envConfig = getEnvironmentConfig(env);
  const mergedConfig = deepMerge(baseConfig, envConfig);
  return mergedConfig;
}
__name(buildConfig, "buildConfig");
buildConfig();
function createConfig(env) {
  return buildConfig(env);
}
__name(createConfig, "createConfig");

// src/utils/unified-resize.ts
var _IframeContentResizeManager = class _IframeContentResizeManager {
  constructor(options = {}) {
    __publicField(this, "options");
    __publicField(this, "resizeObserver");
    __publicField(this, "debounceTimeout");
    __publicField(this, "lastHeight");
    __publicField(this, "lastWidth");
    __publicField(this, "animationFrameId");
    __publicField(this, "isDestroyed");
    __publicField(this, "debouncedDimensionUpdate");
    this.options = options;
    this.resizeObserver = null;
    this.debounceTimeout = null;
    this.lastHeight = 0;
    this.lastWidth = 0;
    this.animationFrameId = null;
    this.isDestroyed = false;
    this.debouncedDimensionUpdate = () => {
      if (this.isDestroyed) return;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = window.setTimeout(() => {
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
        }
        this.animationFrameId = requestAnimationFrame(() => {
          this.calculateAndSendDimensions();
        });
      }, this.options.debounceMs);
    };
    this.options = {
      minHeight: 100,
      maxHeight: Infinity,
      minWidth: 0,
      maxWidth: Infinity,
      debounceMs: 16,
      ...options
    };
    this.initialize();
  }
  initialize() {
    if (typeof window === "undefined" || !window.parent || window.parent === window) {
      console.warn("IframeContentResizeManager should only be used inside an iframe");
      return;
    }
    this.setupResizeObserver();
    this.setupMessageListener();
    this.calculateAndSendDimensions();
  }
  setupResizeObserver() {
    if (!window.ResizeObserver) {
      console.warn("ResizeObserver not supported, falling back to mutation observer");
      this.setupFallbackObserver();
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.debouncedDimensionUpdate();
    });
    this.resizeObserver.observe(document.documentElement);
    this.resizeObserver.observe(document.body);
    const observedElements = document.querySelectorAll("[data-observe-resize]");
    observedElements.forEach((el) => {
      this.resizeObserver.observe(el);
    });
  }
  setupFallbackObserver() {
    const mutationObserver = new MutationObserver(() => {
      this.debouncedDimensionUpdate();
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
    window.addEventListener("resize", () => {
      this.debouncedDimensionUpdate();
    });
  }
  setupMessageListener() {
    const messageHandler = /* @__PURE__ */ __name((event) => {
      if (this.isDestroyed) return;
      try {
        const { data } = event;
        if (!data || typeof data !== "object") return;
        if (data.type === "getHeight") {
          if (!this.options.elementId || data.elementId === this.options.elementId) {
            this.calculateAndSendDimensions();
          }
        }
        if (data.type === "constraintsUpdate" && data.constraints) {
          if (!this.options.elementId || data.elementId === this.options.elementId) {
            this.updateConstraints(data.constraints);
          }
        }
      } catch (error) {
        console.error("Error handling resize message:", error);
      }
    }, "messageHandler");
    window.addEventListener("message", messageHandler);
  }
  calculateAndSendDimensions() {
    if (this.isDestroyed) return;
    try {
      const bodyHeight = document.body.offsetHeight;
      const htmlHeight = document.documentElement.offsetHeight;
      const bodyScrollHeight = document.body.scrollHeight;
      const htmlScrollHeight = document.documentElement.scrollHeight;
      let height = Math.max(bodyHeight, htmlHeight, bodyScrollHeight, htmlScrollHeight);
      height = Math.max(height, this.options.minHeight || 100);
      height = Math.min(height, this.options.maxHeight || Infinity);
      const width = Math.max(document.body.offsetWidth, document.documentElement.offsetWidth);
      const heightChanged = Math.abs(height - this.lastHeight) > 1;
      const widthChanged = Math.abs(width - this.lastWidth) > 1;
      if (heightChanged || widthChanged) {
        this.lastHeight = height;
        this.lastWidth = width;
        const message = {
          type: "resize",
          height,
          width
        };
        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.error("Error calculating dimensions:", error);
    }
  }
  updateConstraints(constraints) {
    this.options = {
      ...this.options,
      ...constraints
    };
    this.calculateAndSendDimensions();
  }
  destroy() {
    this.isDestroyed = true;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
};
__name(_IframeContentResizeManager, "IframeContentResizeManager");
var IframeContentResizeManager = _IframeContentResizeManager;
var _ParentResizeManager = class _ParentResizeManager {
  constructor(iframe, onResize, options = {}) {
    __publicField(this, "iframe");
    __publicField(this, "onResize");
    __publicField(this, "options");
    __publicField(this, "messageHandler");
    __publicField(this, "heightRequestInterval");
    __publicField(this, "isDestroyed");
    this.iframe = iframe;
    this.onResize = onResize;
    this.options = options;
    this.messageHandler = null;
    this.heightRequestInterval = null;
    this.isDestroyed = false;
    this.options = {
      debounceMs: 16,
      ...options
    };
    this.initialize();
  }
  initialize() {
    if (!this.iframe?.contentWindow) {
      console.warn("Iframe or its contentWindow is not available");
      return;
    }
    this.setupMessageHandler();
    this.requestInitialHeight();
  }
  setupMessageHandler() {
    this.messageHandler = (event) => {
      if (this.isDestroyed) return;
      try {
        if (event.source !== this.iframe.contentWindow) {
          return;
        }
        const { data } = event;
        if (!data || data.type !== "resize") {
          return;
        }
        if (typeof data.height !== "number") {
          return;
        }
        if (this.options.elementId && data.elementId !== this.options.elementId) {
          return;
        }
        let { height, width = 0 } = data;
        height = Math.max(height, this.options.minHeight || 0);
        height = Math.min(height, this.options.maxHeight || Infinity);
        this.onResize({
          height,
          width
        });
      } catch (error) {
        console.error("Error handling resize message:", error);
      }
    };
    window.addEventListener("message", this.messageHandler);
  }
  requestInitialHeight() {
    const requestHeight = /* @__PURE__ */ __name(() => {
      if (this.isDestroyed || !this.iframe?.contentWindow) return;
      try {
        const message = {
          type: "getHeight"
        };
        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }
        this.iframe.contentWindow.postMessage(message, "*");
      } catch (error) {
        console.error("Error requesting height:", error);
      }
    }, "requestHeight");
    if (this.iframe.contentDocument?.readyState === "complete") {
      setTimeout(requestHeight, 100);
    } else {
      this.iframe.addEventListener("load", () => {
        setTimeout(requestHeight, 100);
      }, {
        once: true
      });
    }
  }
  updateConstraints(constraints) {
    this.options = {
      ...this.options,
      ...constraints
    };
    if (this.iframe?.contentWindow) {
      try {
        const message = {
          type: "constraintsUpdate",
          constraints
        };
        if (this.options.elementId) {
          message.elementId = this.options.elementId;
        }
        this.iframe.contentWindow.postMessage(message, "*");
      } catch (error) {
        console.error("Error sending constraint update:", error);
      }
    }
    this.recalculateSize();
  }
  recalculateSize() {
    if (this.isDestroyed || !this.iframe?.contentWindow) {
      console.warn("Cannot recalculate size: iframe not available or manager destroyed");
      return;
    }
    try {
      const message = {
        type: "getHeight"
      };
      if (this.options.elementId) {
        message.elementId = this.options.elementId;
      }
      this.iframe.contentWindow.postMessage(message, "*");
      if (this.options.maxHeight && this.options.maxHeight !== Infinity) {
        const constrainedHeight = this.options.maxHeight;
        this.onResize({
          height: constrainedHeight,
          width: this.iframe.offsetWidth || 0
        });
      }
    } catch (error) {
      console.error("Error requesting size recalculation:", error);
    }
  }
  destroy() {
    this.isDestroyed = true;
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    if (this.heightRequestInterval) {
      clearInterval(this.heightRequestInterval);
      this.heightRequestInterval = null;
    }
  }
};
__name(_ParentResizeManager, "ParentResizeManager");
var ParentResizeManager = _ParentResizeManager;
function setupUnifiedContentResize(options = {}) {
  const manager = new IframeContentResizeManager(options);
  return () => manager.destroy();
}
__name(setupUnifiedContentResize, "setupUnifiedContentResize");
function setupUnifiedParentResize(iframe, onResize, options = {}) {
  const manager = new ParentResizeManager(iframe, onResize, options);
  return {
    cleanup: /* @__PURE__ */ __name(() => manager.destroy(), "cleanup"),
    manager
  };
}
__name(setupUnifiedParentResize, "setupUnifiedParentResize");

// src/core.ts
var DEFAULT_IFRAME_STYLES = {
  border: "0",
  margin: "0",
  padding: "0",
  width: "100%",
  height: "100%",
  overflow: "auto",
  display: "block",
  background: "transparent"
};
function getElementsBaseUrl(env) {
  const config2 = createConfig(env);
  return config2["elements"]?.ui?.baseUrl || "";
}
__name(getElementsBaseUrl, "getElementsBaseUrl");
var ENVIRONMENT_URLS = {
  production: getElementsBaseUrl("production"),
  development: getElementsBaseUrl("development"),
  stage: getElementsBaseUrl("stage"),
  local: getElementsBaseUrl("local") || "http://localhost:4000"
  // Default for local development
};
var _SmarterElements = class _SmarterElements {
  constructor(options = {}) {
    __publicField(this, "options");
    __publicField(this, "_mountedElements");
    this.options = {
      publicApiKey: null,
      environment: options.environment || "production",
      ...options
    };
    if (!options?.baseUrl) {
      const env = this.options.environment;
      this.options.baseUrl = ENVIRONMENT_URLS[env];
    }
    this._mountedElements = /* @__PURE__ */ new Map();
  }
  /**
  * Create an element instance
  * @param type - Element type (route name)
  * @param options - Element options
  * @returns ElementInstance
  */
  create(type, options = {}) {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 6);
    const randomHash = timestamp.slice(-6) + random;
    const elementId = `smarter-element-${randomHash}`;
    const instance = new ElementInstance(elementId, type, {
      ...this.options,
      ...options
    });
    this._mountedElements.set(elementId, instance);
    return instance;
  }
  /**
  * Track a mounted element instance
  * @param elementId - The ID of the mounted element
  * @param instance - The element instance
  */
  trackMountedElement(elementId, instance) {
    this._mountedElements.set(elementId, instance);
  }
};
__name(_SmarterElements, "SmarterElements");
var SmarterElements = _SmarterElements;
var _ElementInstance = class _ElementInstance {
  constructor(elementId, type, options = {}) {
    __publicField(this, "elementId");
    __publicField(this, "type");
    __publicField(this, "options");
    __publicField(this, "container");
    __publicField(this, "iframe");
    __publicField(this, "iframeLoaded");
    __publicField(this, "_eventHandlers");
    __publicField(this, "_pendingUpdates");
    __publicField(this, "_initialHeight", 0);
    __publicField(this, "_cleanupResizeHandling", null);
    __publicField(this, "_resizeManager", null);
    __publicField(this, "_environment");
    __publicField(this, "_constraints", {});
    this.elementId = elementId;
    this.type = type;
    this.options = options;
    this.container = null;
    this._environment = options["environment"] || "production";
    this.iframe = null;
    this.iframeLoaded = false;
    this._eventHandlers = {};
    this._pendingUpdates = null;
    this._initialHeight = 0;
  }
  /**
  * Mount the element to a DOM container
  * @param container - DOM element or selector
  */
  mount(container) {
    if (typeof container === "string") {
      const element = document.querySelector(container);
      if (!element) {
        throw new Error(`Container element not found: ${container}`);
      }
      this.container = element;
    } else {
      this.container = container;
    }
    this._loadElementRoute();
  }
  /**
  * Load the element route in the iframe
  * @private
  */
  _loadElementRoute() {
    if (!this.container) return;
    this.iframe = document.createElement("iframe");
    Object.assign(this.iframe.style, DEFAULT_IFRAME_STYLES);
    this.iframe.setAttribute("title", `SmarterElements: ${this.type}`);
    this.iframe.setAttribute("allow", "camera; microphone; fullscreen");
    this.iframe.setAttribute("allowfullscreen", "true");
    this.iframe.setAttribute("id", `${this.elementId}`);
    this.iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-orientation-lock allow-presentation");
    if (this.container) {
      const containerHeight = this.container.offsetHeight;
      if (containerHeight > 0) {
        this._initialHeight = containerHeight;
        this.iframe.style.height = `${containerHeight}px`;
        console.log("Setting initial iframe height from container:", containerHeight);
      }
    }
    this._setupResizeHandling();
    const messageHandler = /* @__PURE__ */ __name((event) => {
      if (!event.data || event.data.source !== "smarter-elements") return;
      if (event.data.elementId !== this.elementId) return;
      const { type, payload } = event.data;
      console.log("Received message from iframe:", event.data);
      this.trigger(type, payload);
    }, "messageHandler");
    window.addEventListener("message", messageHandler);
    const loadHandler = /* @__PURE__ */ __name(() => {
      this.iframeLoaded = true;
      this._onReady();
      if (this._pendingUpdates) {
        this.update(this._pendingUpdates);
        this._pendingUpdates = null;
      }
    }, "loadHandler");
    this.iframe.addEventListener("load", loadHandler, {
      once: true
    });
    const errorHandler = /* @__PURE__ */ __name((error) => {
      console.error("SmarterElements iframe error:", error);
      this.trigger("error", {
        error: error.message || "Unknown error occurred"
      });
    }, "errorHandler");
    this.iframe.addEventListener("error", errorHandler);
    const cleanup = /* @__PURE__ */ __name(() => {
      window.removeEventListener("message", messageHandler);
      this.iframe?.removeEventListener("load", loadHandler);
      this.iframe?.removeEventListener("error", errorHandler);
    }, "cleanup");
    this.container.innerHTML = "";
    this.container.appendChild(this.iframe);
    const unloadHandler = /* @__PURE__ */ __name(() => {
      cleanup();
      window.removeEventListener("unload", unloadHandler);
    }, "unloadHandler");
    window.addEventListener("unload", unloadHandler);
    this.iframe.src = this._getElementUrl();
  }
  /**
  * Get the URL for the element
  * @private
  */
  _getElementUrl() {
    const baseUrl = this.options["baseUrl"] || "";
    const payload = {
      elementId: this.elementId
    };
    Object.entries(this.options).forEach(([key, value]) => {
      if (key !== "baseUrl" && key !== "environment" && key !== "apiKey") {
        payload[key] = value;
      }
    });
    payload["_initialHeight"] = this._initialHeight;
    payload["_environment"] = this._environment;
    const token = createJWT(payload);
    return `${baseUrl}/elements/${this.type}?data=${token}`;
  }
  /**
  * Called when the element is ready
  * @private
  */
  _onReady() {
    this.trigger("ready");
  }
  /**
  * Set up unified resize handling for the iframe
  * @private
  */
  _setupResizeHandling() {
    if (!this.iframe) return;
    const { cleanup, manager } = setupUnifiedParentResize(this.iframe, ({ height, width }) => {
      this._updateIframeHeight(height);
      this.trigger("resize", {
        height,
        width,
        elementId: this.elementId
      });
    }, {
      elementId: this.elementId,
      ...this._constraints
    });
    this._cleanupResizeHandling = cleanup;
    this._resizeManager = manager;
    const messageHandler = /* @__PURE__ */ __name((event) => {
      if (!event.data || event.source !== this.iframe?.contentWindow) return;
      if (event.data.elementId && event.data.elementId !== this.elementId) return;
      const { type, payload, event: eventName, data } = event.data;
      if (type === "event" && eventName) {
        this.trigger(eventName, data);
      } else if (type && type !== "resize") {
        this.trigger(type, payload || data);
      }
    }, "messageHandler");
    window.addEventListener("message", messageHandler);
    const originalCleanup = this._cleanupResizeHandling;
    this._cleanupResizeHandling = () => {
      if (originalCleanup) originalCleanup();
      window.removeEventListener("message", messageHandler);
    };
  }
  /**
  * Update the iframe height with constraint enforcement
  * @param height - New height in pixels
  * @private
  */
  _updateIframeHeight(height) {
    if (!this.iframe) return;
    const minHeight = this._constraints.minHeight || 60;
    const maxHeight = this._constraints.maxHeight || Infinity;
    let constrainedHeight = Math.max(height, minHeight);
    constrainedHeight = Math.min(constrainedHeight, maxHeight);
    const currentHeight = parseInt(this.iframe.style.height, 10) || 0;
    if (Math.abs(currentHeight - constrainedHeight) > 1) {
      if (!this.iframe.style.transition) {
        this.iframe.style.transition = "height 0.15s ease-out";
      }
      this.iframe.style.height = `${constrainedHeight}px`;
    }
  }
  /**
  * Update element options
  * @param options - New options
  */
  update(options) {
    this.options = {
      ...this.options,
      ...options
    };
    if (!this.iframeLoaded) {
      this._pendingUpdates = options;
      return;
    }
    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage({
        source: "smarter-elements",
        type: "update",
        elementId: this.elementId,
        options: this.options
      }, "*");
    }
  }
  /**
  * Unmount the element
  */
  unmount() {
    if (this.container && this.iframe) {
      if (this._cleanupResizeHandling) {
        this._cleanupResizeHandling();
        this._cleanupResizeHandling = null;
      }
      this.container.removeChild(this.iframe);
      this.iframe = null;
      this.iframeLoaded = false;
      this.trigger("unmount");
    }
  }
  /**
  * Add event listener
  * @param event - Event name
  * @param handler - Event handler
  */
  on(event, handler) {
    if (!this._eventHandlers[event]) {
      this._eventHandlers[event] = [];
    }
    this._eventHandlers[event].push(handler);
  }
  /**
  * Remove event listener
  * @param event - Event name
  * @param handler - Event handler to remove
  */
  off(event, handler) {
    if (!this._eventHandlers[event]) return;
    const index = this._eventHandlers[event].indexOf(handler);
    if (index > -1) {
      this._eventHandlers[event].splice(index, 1);
    }
  }
  /**
  * Trigger event
  * @param event - Event name
  * @param data - Event data
  */
  trigger(event, data) {
    if (!this._eventHandlers[event]) return;
    this._eventHandlers[event].forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in ${event} handler:`, error);
      }
    });
  }
  /**
  * Render the component
  * @private
  */
  // Marked as private and unused for now
  // private render(): void {
  //   if (!this.iframeLoaded) return;
  //   if (this.iframe?.contentWindow) {
  //     this.iframe.contentWindow.postMessage(
  //       {
  //         source: 'smarter-elements',
  //         type: 'render',
  //         elementId: this.elementId,
  //         options: this.options,
  //       },
  //       '*'
  //     );
  //   }
  // }
  /**
  * Open a modal with the element content
  * @param options - Modal options including width, height, maxWidth, maxHeight, dismissOnDocumentClick, escapeClose, zIndex
  * @returns Promise that resolves when modal is opened
  */
  async openModal(options = {}) {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.originalOverflow = originalOverflow;
    const modalContainer = document.createElement("div");
    modalContainer.id = `modal-container-${this.elementId}`;
    modalContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: ${options.zIndex || 9999};
      font-family: system-ui, -apple-system, sans-serif;
    `;
    const modalContent = document.createElement("div");
    const modalPadding = options.padding || "10px";
    modalContent.style.cssText = `
      position: relative;
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      width: ${options.width || "90%"};
      ${options.height ? `height: ${options.height};` : "height: auto;"}
      max-width: ${options.maxWidth || "800px"};
      max-height: ${options.maxHeight || "90vh"};
      overflow: ${options.height ? "auto" : "hidden"};
      animation: modalSlideIn 0.3s ease-out;
      padding: ${modalPadding};
      box-sizing: border-box;
    `;
    if (!document.getElementById("modal-animations")) {
      const style = document.createElement("style");
      style.id = "modal-animations";
      style.textContent = `
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
    if (options.dismissOnDocumentClick !== false) {
      modalContainer.addEventListener("click", (e) => {
        if (e.target === modalContainer) {
          this.closeModal();
        }
      });
    }
    if (options.escapeClose !== false) {
      const handleEscape = /* @__PURE__ */ __name((e) => {
        if (e.key === "Escape") {
          this.closeModal();
          document.removeEventListener("keydown", handleEscape);
        }
      }, "handleEscape");
      document.addEventListener("keydown", handleEscape);
      this.escapeHandler = handleEscape;
    }
    const iframe = document.createElement("iframe");
    iframe.setAttribute("allowfullscreen", "true");
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 12px;
    `;
    const modalParams = {
      modal: "true",
      modalWidth: options.width,
      modalHeight: options.height,
      modalMaxWidth: options.maxWidth,
      modalMaxHeight: options.maxHeight,
      modalDismissOnDocumentClick: options.dismissOnDocumentClick,
      modalEscapeClose: options.escapeClose,
      modalZIndex: options.zIndex
    };
    const elementUrl = this._getElementUrl();
    const url = new URL(elementUrl, window.location.origin);
    Object.entries(modalParams).forEach(([key, value]) => {
      if (value !== void 0) {
        url.searchParams.set(key, String(value));
      }
    });
    iframe.src = url.toString();
    const handleModalMessages = /* @__PURE__ */ __name((event) => {
      if (event.source !== iframe.contentWindow) return;
      try {
        const { type: messageType, ...data } = event.data;
        if (data.elementId !== this.elementId) return;
        switch (messageType) {
          case "resize":
            const newHeight = data.height || 400;
            iframe.style.height = `${newHeight}px`;
            const maxWidth = options.maxWidth ? typeof options.maxWidth === "number" ? options.maxWidth : parseInt(options.maxWidth) : 800;
            const maxHeight = options.maxHeight ? typeof options.maxHeight === "number" ? options.maxHeight : options.maxHeight.includes("vh") ? parseInt(options.maxHeight) / 100 * window.innerHeight : parseInt(options.maxHeight) : window.innerHeight * 0.9;
            const newWidth = data.width || modalContent.offsetWidth;
            const constrainedWidth = Math.min(newWidth, maxWidth);
            modalContent.style.width = options.width ? typeof options.width === "number" ? `${options.width}px` : options.width : `${constrainedWidth}px`;
            const paddingValue = parseInt(modalPadding) * 2;
            const totalContentHeight = newHeight + paddingValue;
            const constrainedHeight = Math.min(totalContentHeight, maxHeight);
            modalContent.style.height = `${constrainedHeight}px`;
            modalContent.style.maxHeight = `${maxHeight}px`;
            this.trigger("resize", {
              height: newHeight,
              width: newWidth,
              elementId: this.elementId
            });
            break;
          case "ready":
            this.trigger("ready", {
              elementId: this.elementId
            });
            break;
          case "error":
            this.trigger("error", {
              elementId: this.elementId,
              error: data
            });
            break;
          default:
            this.trigger(messageType, data);
            break;
        }
      } catch (error) {
        console.error("Error handling modal message:", error);
      }
    }, "handleModalMessages");
    window.addEventListener("message", handleModalMessages);
    modalContent.appendChild(iframe);
    modalContainer.appendChild(modalContent);
    document.body.appendChild(modalContainer);
    this.modalContainer = modalContainer;
    this.modalIframe = iframe;
    this.modalMessageHandler = handleModalMessages;
    this.trigger("modal:open", {
      elementId: this.elementId
    });
    return this;
  }
  /**
  * Close the modal
  * @returns The element instance for chaining
  */
  closeModal() {
    const modalContainer = this.modalContainer;
    if (modalContainer && modalContainer.parentNode) {
      const originalOverflow = this.originalOverflow;
      if (originalOverflow !== void 0) {
        document.body.style.overflow = originalOverflow;
        this.originalOverflow = void 0;
      }
      if (this.escapeHandler) {
        document.removeEventListener("keydown", this.escapeHandler);
        this.escapeHandler = null;
      }
      if (this.modalMessageHandler) {
        window.removeEventListener("message", this.modalMessageHandler);
        this.modalMessageHandler = null;
      }
      modalContainer.style.opacity = "0";
      modalContainer.style.transition = "opacity 0.2s ease-out";
      setTimeout(() => {
        if (modalContainer.parentNode) {
          modalContainer.parentNode.removeChild(modalContainer);
        }
        this.modalContainer = null;
        this.modalIframe = null;
      }, 200);
      this.trigger("modal:close", {
        elementId: this.elementId
      });
    }
    return this;
  }
  /**
  * Update element constraints (min/max dimensions)
  * @param constraints - New constraints to apply
  * @returns The element instance for chaining
  */
  updateConstraints(constraints) {
    this._constraints = {
      ...this._constraints,
      ...constraints
    };
    if (this.iframe) {
      const { minHeight, maxHeight, minWidth, maxWidth } = this._constraints;
      if (minHeight !== void 0) {
        this.iframe.style.minHeight = `${minHeight}px`;
      }
      if (maxHeight !== void 0) {
        this.iframe.style.maxHeight = `${maxHeight}px`;
      }
      if (minWidth !== void 0) {
        this.iframe.style.minWidth = `${minWidth}px`;
      }
      if (maxWidth !== void 0) {
        this.iframe.style.maxWidth = `${maxWidth}px`;
      }
      this.iframe.style.maxWidth = "100%";
    }
    if (this._cleanupResizeHandling) {
      this._cleanupResizeHandling();
      this._setupResizeHandling();
    }
    this.trigger("constraints:update", {
      constraints: this._constraints,
      elementId: this.elementId
    });
    return this;
  }
  /**
  * Manually trigger size recalculation
  * Useful when container size changes (e.g., window resize, sidebar toggle)
  * @returns The element instance for chaining
  */
  recalculateSize() {
    if (this._resizeManager) {
      this._resizeManager.recalculateSize();
    } else {
      console.warn("Cannot recalculate size: resize manager not available. Element may not be mounted.");
    }
    return this;
  }
  /**
  * Get current element constraints
  * @returns Current constraints
  */
  getConstraints() {
    return {
      ...this._constraints
    };
  }
};
__name(_ElementInstance, "ElementInstance");
var ElementInstance = _ElementInstance;
function createSmarterElements(options = {}) {
  return new SmarterElements(options);
}
__name(createSmarterElements, "createSmarterElements");

// src/factory.ts
var SmarterElementsFactory = /* @__PURE__ */ __name(function(options = {}) {
  const instance = createSmarterElements(options);
  const factory = /* @__PURE__ */ __name((type, elementOptions = {}) => {
    return instance.create(type, elementOptions);
  }, "factory");
  factory.create = (type, elementOptions = {}) => {
    return instance.create(type, elementOptions);
  };
  Object.defineProperty(factory, "instance", {
    value: instance,
    writable: false,
    enumerable: false,
    configurable: false
  });
  return factory;
}, "SmarterElementsFactory");
if (typeof window !== "undefined") {
  const SmarterElementsMain = /* @__PURE__ */ __name(function(options = {}) {
    return SmarterElementsFactory(options);
  }, "SmarterElementsMain");
  SmarterElementsMain.init = function(options = {}) {
    return SmarterElementsFactory(options);
  };
  SmarterElementsMain.create = function(type, options = {}) {
    return SmarterElementsFactory({}).create(type, options);
  };
  SmarterElementsMain.SmarterElements = SmarterElementsFactory;
  window.SmarterElements = SmarterElementsMain;
  console.debug("SmarterElements exposed on window:", window.SmarterElements);
}
var factory_default = SmarterElementsFactory;
var SmarterElement = /* @__PURE__ */ forwardRef(({ type, config: config2 = {}, constraints = {}, environment = "development", baseUrl, onReady, onResize, onError, onEvent, style = {}, className = "", autoResize = false }, ref) => {
  const iframeRef = useRef(null);
  const [dimensions, setDimensions] = useState(null);
  const elementIdRef = useRef(`smarter-element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const constraintToCSS = /* @__PURE__ */ __name((value) => {
    if (typeof value === "number") return `${value}px`;
    return value;
  }, "constraintToCSS");
  const getIframeUrl = /* @__PURE__ */ __name(() => {
    const base = baseUrl || (environment === "production" ? "https://elements.smarterservices.com" : "/elements");
    const elementPath = type.startsWith("blockkit/") ? "blockkit/renderer" : type;
    const jwt = createJWT({
      ...type.startsWith("blockkit/") ? {
        blocksJson: JSON.stringify(config2)
      } : config2,
      _constraints: constraints,
      elementId: elementIdRef.current
    });
    return `${base}/${elementPath}?data=${encodeURIComponent(jwt)}`;
  }, "getIframeUrl");
  useEffect(() => {
    const handleMessage = /* @__PURE__ */ __name((event) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;
      try {
        const { type: messageType, ...data } = event.data;
        switch (messageType) {
          case "resize":
            if (data.elementId === elementIdRef.current) {
              const newDimensions = {
                height: data.height || 0,
                width: data.width || 0,
                elementId: data.elementId
              };
              setDimensions({
                height: newDimensions.height,
                width: newDimensions.width
              });
              onResize?.(newDimensions);
            }
            break;
          case "ready":
            if (data.elementId === elementIdRef.current) {
              onReady?.();
            }
            break;
          case "error":
            if (data.elementId === elementIdRef.current) {
              const error = new Error(data.message || "Element error");
              onError?.(error);
            }
            break;
          default:
            if (data.elementId === elementIdRef.current) {
              onEvent?.(messageType, data);
            }
            break;
        }
      } catch (error) {
        console.error("Error handling message from SmarterElement:", error);
        onError?.(error);
      }
    }, "handleMessage");
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    onReady,
    onResize,
    onError,
    onEvent
  ]);
  useImperativeHandle(ref, () => ({
    updateConfig: /* @__PURE__ */ __name((newConfig) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: "update-config",
          config: newConfig,
          elementId: elementIdRef.current
        }, "*");
      }
    }, "updateConfig"),
    updateConstraints: /* @__PURE__ */ __name((newConstraints) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: "update-constraints",
          constraints: newConstraints,
          elementId: elementIdRef.current
        }, "*");
      }
    }, "updateConstraints"),
    getDimensions: /* @__PURE__ */ __name(() => dimensions, "getDimensions"),
    sendMessage: /* @__PURE__ */ __name((type2, data) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: type2,
          ...data,
          elementId: elementIdRef.current
        }, "*");
      }
    }, "sendMessage"),
    getIframe: /* @__PURE__ */ __name(() => iframeRef.current, "getIframe")
  }), [
    dimensions
  ]);
  const iframeStyles = {
    border: "none",
    borderRadius: "4px",
    transition: "height 0.15s ease-out, width 0.15s ease-out",
    ...style
  };
  if (autoResize) {
    iframeStyles.width = "100%";
  } else if (constraints.maxWidth) {
    iframeStyles.width = constraintToCSS(constraints.maxWidth);
    iframeStyles.maxWidth = constraintToCSS(constraints.maxWidth);
  }
  if (constraints.minWidth) {
    iframeStyles.minWidth = constraintToCSS(constraints.minWidth);
  }
  iframeStyles.maxWidth = "100%";
  if (autoResize && dimensions) {
    iframeStyles.height = `${dimensions.height}px`;
  } else {
    if (constraints.maxHeight) {
      iframeStyles.maxHeight = constraintToCSS(constraints.maxHeight);
    }
    if (constraints.minHeight) {
      iframeStyles.minHeight = constraintToCSS(constraints.minHeight);
    }
    if (dimensions && dimensions.height > 0) {
      let calculatedHeight = dimensions.height;
      if (constraints.maxHeight && typeof constraints.maxHeight === "number") {
        calculatedHeight = Math.min(calculatedHeight, constraints.maxHeight);
      }
      if (constraints.minHeight && typeof constraints.minHeight === "number") {
        calculatedHeight = Math.max(calculatedHeight, constraints.minHeight);
      }
      iframeStyles.height = `${calculatedHeight}px`;
    } else if (constraints.minHeight) {
      iframeStyles.height = constraintToCSS(constraints.minHeight);
    } else {
      iframeStyles.height = "300px";
    }
  }
  return /* @__PURE__ */ React3.createElement("iframe", {
    ref: iframeRef,
    src: getIframeUrl(),
    style: iframeStyles,
    className,
    title: `SmarterElement: ${type}`,
    allow: "camera; microphone; fullscreen",
    sandbox: "allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
  });
});
SmarterElement.displayName = "SmarterElement";

// src/utils/event-schemas.ts
function validateType(value, type, required = true) {
  if (value === void 0 || value === null) {
    return !required;
  }
  switch (type) {
    case "string":
      return typeof value === "string";
    case "number":
      return typeof value === "number" && !isNaN(value);
    case "boolean":
      return typeof value === "boolean";
    case "object":
      return typeof value === "object" && !Array.isArray(value);
    case "array":
      return Array.isArray(value);
    case "date":
      return value instanceof Date || typeof value === "string" && !isNaN(Date.parse(value));
    case "id":
      return typeof value === "string" && value.trim().length > 0;
    default:
      return false;
  }
}
__name(validateType, "validateType");
function validatePayload(payload, schema) {
  const errors = [];
  const result = {
    valid: true,
    errors
  };
  Object.entries(schema.fields).forEach(([field, fieldDef]) => {
    const value = payload[field];
    const isRequired = fieldDef.required !== false;
    if (isRequired && (value === void 0 || value === null)) {
      errors.push(`Missing required field: ${field}`);
      result.valid = false;
      return;
    }
    if (value === void 0 || value === null) {
      return;
    }
    if (fieldDef.type === "object" && fieldDef.fields) {
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        errors.push(`Field '${field}' must be an object`);
        result.valid = false;
      } else {
        const nestedSchema = {
          ...fieldDef,
          fields: fieldDef.fields,
          description: fieldDef.description || ""};
        const nestedResult = validatePayload(value, nestedSchema);
        if (!nestedResult.valid && nestedResult.errors) {
          errors.push(...nestedResult.errors.map((e) => `${field}.${e}`));
          result.valid = false;
        }
      }
    } else if (fieldDef.type === "array" && fieldDef.itemType) {
      if (!Array.isArray(value)) {
        errors.push(`Field '${field}' must be an array`);
        result.valid = false;
      } else if (typeof fieldDef.itemType === "string") {
        value.forEach((item, index) => {
          if (!validateType(item, fieldDef.itemType, true)) {
            errors.push(`Item at index ${index} in '${field}' array has invalid type`);
            result.valid = false;
          }
        });
      } else {
        value.forEach((item, index) => {
          if (typeof item !== "object" || item === null) {
            errors.push(`Item at index ${index} in '${field}' must be an object`);
            result.valid = false;
          } else {
            const itemResult = validatePayload(item, {
              ...fieldDef.itemType,
              fields: fieldDef.itemType.fields || {}
            });
            if (!itemResult.valid && itemResult.errors) {
              errors.push(...itemResult.errors.map((e) => `${field}[${index}].${e}`));
              result.valid = false;
            }
          }
        });
      }
    } else {
      if (!validateType(value, fieldDef.type, isRequired)) {
        errors.push(`Field '${field}' must be of type ${fieldDef.type}`);
        result.valid = false;
      }
    }
  });
  return result;
}
__name(validatePayload, "validatePayload");
var eventSchemas = {
  "exam-selected": {
    type: "object",
    description: "Fired when an exam is selected in the UI",
    example: {
      examId: "exam-123",
      examName: "Midterm Exam",
      courseId: "course-456",
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    },
    fields: {
      examId: {
        type: "id",
        description: "Unique identifier for the exam"
      },
      examName: {
        type: "string",
        description: "Display name of the exam"
      },
      courseId: {
        type: "id",
        description: "Unique identifier for the course"
      },
      timestamp: {
        type: "date",
        description: "When the selection occurred"
      }
    }
  }
};
function validateElementEvent(eventName, payload) {
  const schema = eventSchemas[eventName];
  if (!schema) {
    return {
      valid: false,
      errors: [
        `No schema found for event: ${eventName}`
      ]
    };
  }
  return validatePayload(payload, schema);
}
__name(validateElementEvent, "validateElementEvent");
function getEventDocumentation(eventName) {
  if (eventName) {
    const schema = eventSchemas[eventName];
    if (!schema) {
      throw new Error(`No schema found for event: ${eventName}`);
    }
    return {
      [eventName]: {
        description: schema.description,
        example: schema.example,
        fields: schema.fields
      }
    };
  }
  return Object.entries(eventSchemas).reduce((acc, [name, schema]) => {
    acc[name] = {
      description: schema.description,
      example: schema.example,
      fields: schema.fields
    };
    return acc;
  }, {});
}
__name(getEventDocumentation, "getEventDocumentation");
var event_schemas_default = {
  validateElementEvent,
  getEventDocumentation,
  schemas: eventSchemas
};

// src/utils/event-bridge.ts
function getParentOrigin() {
  return "*";
}
__name(getParentOrigin, "getParentOrigin");
function broadcastElementEvent(eventName, elementType, elementId, data = {}) {
  try {
    const prefixedEventName = `${elementType}:${eventName}`;
    const validation = event_schemas_default.validateElementEvent(prefixedEventName, data);
    if (!validation.valid) {
      console.error("Event validation failed:", validation.errors);
      return {
        success: false,
        errors: (validation.errors || []).map((err) => new Error(err))
      };
    }
    const targetOrigin = getParentOrigin();
    delete data["elementId"];
    delete data["elementType"];
    const message = {
      source: "smarter-elements",
      event: eventName,
      elementType,
      elementId,
      payload: data
    };
    window.parent.postMessage(message, targetOrigin);
    return {
      success: true
    };
  } catch (error) {
    console.error("Error broadcasting event:", error);
    return {
      success: false,
      errors: [
        error
      ]
    };
  }
}
__name(broadcastElementEvent, "broadcastElementEvent");
function listen(eventName, callback) {
  const messageHandler = /* @__PURE__ */ __name((event) => {
    if (event.source !== window.parent) {
      return;
    }
    if (typeof event.data === "object" && event.data !== null && event.data.source === "smarter-elements" && event.data.event === eventName) {
      callback(event.data.payload);
    }
  }, "messageHandler");
  window.addEventListener("message", messageHandler);
  return () => {
    window.removeEventListener("message", messageHandler);
  };
}
__name(listen, "listen");

// src/utils/element-events.ts
function createElementEventBroadcaster(context) {
  const { elementType, elementId } = context;
  return /* @__PURE__ */ __name(function broadcastEvent(eventName, data = {}) {
    return broadcastElementEvent(eventName, elementType, elementId, data);
  }, "broadcastEvent");
}
__name(createElementEventBroadcaster, "createElementEventBroadcaster");
function createElementEvents(elementType, elementId, getEventName) {
  const context = {
    elementType,
    elementId,
    getEventName
  };
  const broadcast = createElementEventBroadcaster(context);
  return {
    broadcast,
    context
  };
}
__name(createElementEvents, "createElementEvents");

// src/utils/register-schemas.ts
function registerElementEventSchemas(elementType, schemas) {
  try {
    const prefixedSchemas = Object.entries(schemas).reduce((acc, [eventName, schema]) => {
      const prefixedName = `${elementType}:${eventName}`;
      acc[prefixedName] = schema;
      return acc;
    }, {});
    Object.assign(eventSchemas, prefixedSchemas);
    console.debug(`Registered ${Object.keys(prefixedSchemas).length} event schemas for ${elementType}`);
    return true;
  } catch (error) {
    console.error(`Failed to register event schemas for ${elementType}:`, error);
    return false;
  }
}
__name(registerElementEventSchemas, "registerElementEventSchemas");
function getElementEventSchemas(elementType) {
  const componentSchemas = {};
  Object.entries(eventSchemas).forEach(([eventName, schema]) => {
    if (eventName.startsWith(`${elementType}:`)) {
      const unprefixedName = eventName.replace(`${elementType}:`, "");
      componentSchemas[unprefixedName] = schema;
    }
  });
  return componentSchemas;
}
__name(getElementEventSchemas, "getElementEventSchemas");

// src/hooks/useElementEvents.ts
function useElementEvents(elementType, elementId, eventSchemas2, getEventName) {
  const elementEvents = useMemo(() => {
    return createElementEvents(elementType, elementId, getEventName);
  }, [
    elementType,
    elementId,
    getEventName
  ]);
  useEffect(() => {
    registerElementEventSchemas(elementType, eventSchemas2);
  }, [
    elementType,
    eventSchemas2
  ]);
  return elementEvents;
}
__name(useElementEvents, "useElementEvents");
function useElement(schema, elementId, options = {}) {
  const { ELEMENT_TYPE, ELEMENT_NAME, eventSchemas: eventSchemas2, getEventName } = schema;
  const { autoLifecycleEvents = true, lifecycleEventData = {} } = options;
  const elementEvents = useElementEvents(
    ELEMENT_TYPE,
    elementId,
    eventSchemas2,
    getEventName
    // This is now optional
  );
  useEffect(() => {
    if (autoLifecycleEvents) {
      elementEvents.broadcast("mounted", {
        timestamp: (/* @__PURE__ */ new Date()).toISOString(),
        elementId,
        ...lifecycleEventData
      });
      return () => {
        elementEvents.broadcast("unmounted", {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          elementId,
          ...lifecycleEventData
        });
      };
    }
    return void 0;
  }, [
    autoLifecycleEvents,
    elementEvents,
    elementId,
    lifecycleEventData
  ]);
  const broadcastError = /* @__PURE__ */ __name((errorMessage, errorCode) => {
    elementEvents.broadcast("error", {
      errorMessage,
      errorCode,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId
    });
  }, "broadcastError");
  const broadcastReady = /* @__PURE__ */ __name(() => {
    elementEvents.broadcast("ready", {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId
    });
  }, "broadcastReady");
  const broadcastStateChange = /* @__PURE__ */ __name((currentState, previousState) => {
    elementEvents.broadcast("stateChanged", {
      currentState,
      previousState,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId
    });
  }, "broadcastStateChange");
  return {
    // Core properties
    elementId,
    elementType: ELEMENT_TYPE,
    elementName: ELEMENT_NAME,
    // Event broadcasting
    events: elementEvents,
    // Helper methods for standard events
    broadcastError,
    broadcastReady,
    broadcastStateChange
  };
}
__name(useElement, "useElement");
function useSmarterElements(options = {}) {
  const { environment = "development", baseUrl, defaultConstraints = {} } = options;
  const elementsRef = useRef(/* @__PURE__ */ new Map());
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);
  useEffect(() => {
    return () => {
      elementsRef.current.forEach((element) => {
        element.destroy();
      });
      elementsRef.current.clear();
    };
  }, []);
  const create = useCallback((type, options2 = {}) => {
    const elementId = `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const { config: config2 = {}, constraints = {}, autoResize = false, onReady, onResize, onError, onEvent } = options2;
    const mergedConstraints = {
      ...defaultConstraints,
      ...constraints
    };
    let iframe = null;
    let container = null;
    let wrapper = null;
    let cleanupResize = null;
    let resizeManager = null;
    const instance = {
      elementId,
      type,
      config: {
        ...config2
      },
      constraints: {
        ...mergedConstraints
      },
      dimensions: null,
      isReady: false,
      mount: /* @__PURE__ */ __name((containerSelector) => {
        if (typeof containerSelector === "string") {
          container = document.querySelector(containerSelector);
          if (!container) {
            throw new Error(`Container not found: ${containerSelector}`);
          }
        } else {
          container = containerSelector;
        }
        const elementPadding = config2["padding"] || "10px";
        wrapper = document.createElement("div");
        wrapper.style.padding = elementPadding;
        wrapper.style.boxSizing = "border-box";
        wrapper.style.borderRadius = "4px";
        wrapper.style.overflow = "hidden";
        iframe = document.createElement("iframe");
        iframe.src = getIframeUrl();
        iframe.style.border = "none";
        iframe.style.transition = "height 0.15s ease-out, width 0.15s ease-out";
        iframe.style.width = "100%";
        iframe.style.display = "block";
        iframe.title = `SmarterElement: ${type}`;
        iframe.allow = "camera; microphone; fullscreen";
        iframe.sandbox.add("allow-scripts", "allow-same-origin", "allow-forms", "allow-popups", "allow-modals");
        applyConstraints();
        setupResizeHandling();
        setupEventHandling();
        wrapper.appendChild(iframe);
        container.appendChild(wrapper);
        triggerUpdate();
      }, "mount"),
      unmount: /* @__PURE__ */ __name(() => {
        if (cleanupResize) {
          cleanupResize();
          cleanupResize = null;
        }
        if (wrapper && container) {
          container.removeChild(wrapper);
        }
        iframe = null;
        wrapper = null;
        container = null;
        triggerUpdate();
      }, "unmount"),
      updateConfig: /* @__PURE__ */ __name((newConfig) => {
        instance.config = {
          ...instance.config,
          ...newConfig
        };
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: "update-config",
            config: instance.config,
            elementId
          }, "*");
        }
        triggerUpdate();
      }, "updateConfig"),
      updateConstraints: /* @__PURE__ */ __name((newConstraints) => {
        instance.constraints = {
          ...instance.constraints,
          ...newConstraints
        };
        applyConstraints();
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: "update-constraints",
            constraints: instance.constraints,
            elementId
          }, "*");
        }
        triggerUpdate();
      }, "updateConstraints"),
      recalculateSize: /* @__PURE__ */ __name(() => {
        if (resizeManager) {
          resizeManager.recalculateSize();
        } else {
          console.warn("Cannot recalculate size: resize manager not available. Element may not be mounted.");
        }
      }, "recalculateSize"),
      sendMessage: /* @__PURE__ */ __name((type2, data) => {
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: type2,
            ...data,
            elementId
          }, "*");
        }
      }, "sendMessage"),
      openModal: /* @__PURE__ */ __name(async (modalOptions = {}) => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        instance.originalOverflow = originalOverflow;
        const modalContainer = document.createElement("div");
        modalContainer.id = `modal-container-${elementId}`;
        modalContainer.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: ${modalOptions.zIndex || 9999};
          font-family: system-ui, -apple-system, sans-serif;
        `;
        const modalContent = document.createElement("div");
        const modalPadding = modalOptions.padding || "10px";
        modalContent.style.cssText = `
          position: relative;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          width: ${modalOptions.width || "90%"};
          ${modalOptions.height ? `height: ${modalOptions.height};` : "height: auto;"}
          max-width: ${modalOptions.maxWidth || "800px"};
          max-height: ${modalOptions.maxHeight || "90vh"};
          overflow: ${modalOptions.height ? "auto" : "hidden"};
          animation: modalSlideIn 0.3s ease-out;
          padding: ${modalPadding};
          box-sizing: border-box;
        `;
        if (!document.getElementById("modal-animations")) {
          const style = document.createElement("style");
          style.id = "modal-animations";
          style.textContent = `
            @keyframes modalSlideIn {
              from { opacity: 0; transform: scale(0.95) translateY(-10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `;
          document.head.appendChild(style);
        }
        if (modalOptions.dismissOnDocumentClick !== false) {
          modalContainer.addEventListener("click", (e) => {
            if (e.target === modalContainer) {
              instance.closeModal();
            }
          });
        }
        if (modalOptions.escapeClose !== false) {
          const handleEscape = /* @__PURE__ */ __name((e) => {
            if (e.key === "Escape") {
              instance.closeModal();
              document.removeEventListener("keydown", handleEscape);
            }
          }, "handleEscape");
          document.addEventListener("keydown", handleEscape);
          instance.escapeHandler = handleEscape;
        }
        const modalIframe = document.createElement("iframe");
        modalIframe.style.cssText = `
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 12px;
        `;
        const modalParams = {
          modal: "true",
          modalWidth: modalOptions.width,
          modalHeight: modalOptions.height,
          modalMaxWidth: modalOptions.maxWidth,
          modalMaxHeight: modalOptions.maxHeight,
          modalDismissOnDocumentClick: modalOptions.dismissOnDocumentClick,
          modalEscapeClose: modalOptions.escapeClose,
          modalZIndex: modalOptions.zIndex
        };
        const elementUrl = getIframeUrl();
        const url = new URL(elementUrl, window.location.origin);
        Object.entries(modalParams).forEach(([key, value]) => {
          if (value !== void 0) {
            url.searchParams.set(key, String(value));
          }
        });
        modalIframe.src = url.toString();
        const handleModalMessages = /* @__PURE__ */ __name((event) => {
          if (event.source !== modalIframe.contentWindow) return;
          try {
            const { type: messageType, ...data } = event.data;
            if (data.elementId !== elementId) return;
            switch (messageType) {
              case "resize":
                const newHeight = data.height || 400;
                modalIframe.style.height = `${newHeight}px`;
                const maxWidth = modalOptions.maxWidth ? typeof modalOptions.maxWidth === "number" ? modalOptions.maxWidth : parseInt(modalOptions.maxWidth) : 800;
                const maxHeight = modalOptions.maxHeight ? typeof modalOptions.maxHeight === "number" ? modalOptions.maxHeight : modalOptions.maxHeight.includes("vh") ? parseInt(modalOptions.maxHeight) / 100 * window.innerHeight : parseInt(modalOptions.maxHeight) : window.innerHeight * 0.9;
                const newWidth = data.width || modalContent.offsetWidth;
                const constrainedWidth = Math.min(newWidth, maxWidth);
                modalContent.style.width = modalOptions.width ? typeof modalOptions.width === "number" ? `${modalOptions.width}px` : modalOptions.width : `${constrainedWidth}px`;
                const paddingValue = parseInt(modalPadding) * 2;
                const totalContentHeight = newHeight + paddingValue;
                const constrainedHeight = Math.min(totalContentHeight, maxHeight);
                modalContent.style.height = `${constrainedHeight}px`;
                modalContent.style.maxHeight = `${maxHeight}px`;
                instance.dimensions = {
                  height: newHeight,
                  width: newWidth
                };
                onResize?.({
                  height: newHeight,
                  width: newWidth,
                  elementId
                });
                break;
              case "ready":
                onReady?.();
                break;
              case "error":
                onError?.(new Error(data.message || "Element error"));
                break;
              default:
                onEvent?.(messageType, data);
                break;
            }
          } catch (error) {
            console.error("Error handling modal message:", error);
          }
        }, "handleModalMessages");
        window.addEventListener("message", handleModalMessages);
        modalContent.appendChild(modalIframe);
        modalContainer.appendChild(modalContent);
        document.body.appendChild(modalContainer);
        instance.modalContainer = modalContainer;
        instance.modalIframe = modalIframe;
        instance.modalMessageHandler = handleModalMessages;
      }, "openModal"),
      closeModal: /* @__PURE__ */ __name(() => {
        const modalContainer = instance.modalContainer;
        if (modalContainer && modalContainer.parentNode) {
          const originalOverflow = instance.originalOverflow;
          if (originalOverflow !== void 0) {
            document.body.style.overflow = originalOverflow;
            instance.originalOverflow = void 0;
          }
          if (instance.escapeHandler) {
            document.removeEventListener("keydown", instance.escapeHandler);
            instance.escapeHandler = null;
          }
          if (instance.modalMessageHandler) {
            window.removeEventListener("message", instance.modalMessageHandler);
            instance.modalMessageHandler = null;
          }
          modalContainer.style.opacity = "0";
          modalContainer.style.transition = "opacity 0.2s ease-out";
          setTimeout(() => {
            if (modalContainer.parentNode) {
              modalContainer.parentNode.removeChild(modalContainer);
            }
            instance.modalContainer = null;
            instance.modalIframe = null;
          }, 200);
        }
      }, "closeModal"),
      destroy: /* @__PURE__ */ __name(() => {
        instance.unmount();
        instance.closeModal();
        elementsRef.current.delete(elementId);
        triggerUpdate();
      }, "destroy")
    };
    function setupResizeHandling() {
      if (!iframe) return;
      const convertConstraint = /* @__PURE__ */ __name((value) => {
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          if (value.includes("vh")) {
            return parseInt(value) / 100 * window.innerHeight;
          }
          if (value.includes("vw")) {
            return parseInt(value) / 100 * window.innerWidth;
          }
          return parseInt(value);
        }
        return void 0;
      }, "convertConstraint");
      const numericConstraints = {
        elementId,
        minHeight: convertConstraint(instance.constraints.minHeight),
        maxHeight: convertConstraint(instance.constraints.maxHeight),
        minWidth: convertConstraint(instance.constraints.minWidth),
        maxWidth: convertConstraint(instance.constraints.maxWidth)
      };
      const { cleanup, manager } = setupUnifiedParentResize(iframe, ({ height, width }) => {
        const newDimensions = {
          height,
          width,
          elementId
        };
        instance.dimensions = {
          height,
          width
        };
        applyConstraints();
        onResize?.(newDimensions);
        triggerUpdate();
      }, numericConstraints);
      cleanupResize = cleanup;
      resizeManager = manager;
    }
    __name(setupResizeHandling, "setupResizeHandling");
    function getIframeUrl() {
      const base = baseUrl || (environment === "production" ? "https://elements.smarterservices.com" : "/elements");
      const elementPath = type.startsWith("blockkit/") ? "blockkit/renderer" : type;
      const jwt = createJWT({
        ...instance.config,
        _constraints: instance.constraints,
        elementId
      });
      return `${base}/${elementPath}?data=${encodeURIComponent(jwt)}`;
    }
    __name(getIframeUrl, "getIframeUrl");
    function applyConstraints() {
      if (!iframe || !wrapper) return;
      const constraintToCSS = /* @__PURE__ */ __name((value) => {
        if (typeof value === "number") return `${value}px`;
        return value;
      }, "constraintToCSS");
      if (autoResize) {
        wrapper.style.width = "100%";
      } else if (instance.constraints.maxWidth) {
        wrapper.style.width = constraintToCSS(instance.constraints.maxWidth);
        wrapper.style.maxWidth = constraintToCSS(instance.constraints.maxWidth);
      }
      if (instance.constraints.minWidth) {
        wrapper.style.minWidth = constraintToCSS(instance.constraints.minWidth);
      }
      wrapper.style.maxWidth = "100%";
      iframe.style.width = "100%";
      if (autoResize && instance.dimensions) {
        iframe.style.height = `${instance.dimensions.height}px`;
      } else {
        if (instance.constraints.maxHeight) {
          iframe.style.maxHeight = constraintToCSS(instance.constraints.maxHeight);
        }
        if (instance.constraints.minHeight) {
          iframe.style.minHeight = constraintToCSS(instance.constraints.minHeight);
        }
        if (instance.dimensions && instance.dimensions.height > 0) {
          let calculatedHeight = instance.dimensions.height;
          if (instance.constraints.maxHeight && typeof instance.constraints.maxHeight === "number") {
            calculatedHeight = Math.min(calculatedHeight, instance.constraints.maxHeight);
          }
          if (instance.constraints.minHeight && typeof instance.constraints.minHeight === "number") {
            calculatedHeight = Math.max(calculatedHeight, instance.constraints.minHeight);
          }
          iframe.style.height = `${calculatedHeight}px`;
        } else if (instance.constraints.minHeight) {
          iframe.style.height = constraintToCSS(instance.constraints.minHeight);
        } else {
          iframe.style.height = "300px";
        }
      }
    }
    __name(applyConstraints, "applyConstraints");
    function setupEventHandling() {
      const handleMessage = /* @__PURE__ */ __name((event) => {
        if (!iframe || event.source !== iframe.contentWindow) return;
        try {
          const { type: messageType, ...data } = event.data;
          if (data.elementId !== elementId) return;
          switch (messageType) {
            case "resize":
              break;
            case "ready":
              instance.isReady = true;
              onReady?.();
              triggerUpdate();
              break;
            case "error":
              const error = new Error(data.message || "Element error");
              onError?.(error);
              break;
            default:
              onEvent?.(messageType, data);
              break;
          }
        } catch (error) {
          console.error("Error handling message from SmarterElement:", error);
          onError?.(error);
        }
      }, "handleMessage");
      window.addEventListener("message", handleMessage);
      const originalDestroy = instance.destroy;
      instance.destroy = () => {
        window.removeEventListener("message", handleMessage);
        originalDestroy();
      };
    }
    __name(setupEventHandling, "setupEventHandling");
    elementsRef.current.set(elementId, instance);
    triggerUpdate();
    return instance;
  }, [
    environment,
    baseUrl,
    defaultConstraints,
    triggerUpdate
  ]);
  const getElements = useCallback(() => {
    return Array.from(elementsRef.current.values());
  }, []);
  const getElement = useCallback((elementId) => {
    return elementsRef.current.get(elementId) || null;
  }, []);
  const destroyAll = useCallback(() => {
    elementsRef.current.forEach((element) => {
      element.destroy();
    });
    elementsRef.current.clear();
    triggerUpdate();
  }, [
    triggerUpdate
  ]);
  return {
    create,
    getElements,
    getElement,
    destroyAll
  };
}
__name(useSmarterElements, "useSmarterElements");

// src/utils/debounce.ts
function debounce(func, wait, immediate = false) {
  let timeout = null;
  let args = null;
  let context = null;
  const later = /* @__PURE__ */ __name(() => {
    timeout = null;
    if (!immediate && args) {
      func.apply(context, args);
      context = args = null;
    }
  }, "later");
  const debounced = /* @__PURE__ */ __name(function(...newArgs) {
    context = this;
    args = newArgs;
    const callNow = immediate && !timeout;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
      context = args = null;
    }
  }, "debounced");
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    context = args = null;
  };
  debounced.flush = () => {
    if (timeout && args) {
      clearTimeout(timeout);
      func.apply(context, args);
      timeout = null;
      context = args = null;
    }
  };
  return debounced;
}
__name(debounce, "debounce");
function throttle(func, wait, options = {}) {
  let timeout = null;
  let previous = 0;
  let args = null;
  let context = null;
  const { leading = true, trailing = true } = options;
  const later = /* @__PURE__ */ __name(() => {
    previous = leading === false ? 0 : Date.now();
    timeout = null;
    if (args) {
      func.apply(context, args);
      context = args = null;
    }
  }, "later");
  const throttled = /* @__PURE__ */ __name(function(...newArgs) {
    const now = Date.now();
    if (!previous && leading === false) previous = now;
    const remaining = wait - (now - previous);
    context = this;
    args = newArgs;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func.apply(context, args);
      context = args = null;
    } else if (!timeout && trailing !== false) {
      timeout = window.setTimeout(later, remaining);
    }
  }, "throttled");
  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    previous = 0;
    context = args = null;
  };
  throttled.flush = () => {
    if (timeout && args) {
      clearTimeout(timeout);
      func.apply(context, args);
      timeout = null;
      previous = Date.now();
      context = args = null;
    }
  };
  return throttled;
}
__name(throttle, "throttle");

// src/schemas/base-element-schemas.ts
var baseElementEvents = {
  "mounted": {
    type: "object",
    description: "Fired when the element is mounted in the DOM",
    example: {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId: "element-123"
    },
    fields: {
      timestamp: {
        type: "date",
        required: true,
        description: "When the element was mounted"
      },
      elementId: {
        type: "string",
        required: true,
        description: "The unique ID of the element instance"
      }
    }
  },
  "unmounted": {
    type: "object",
    description: "Fired when the element is unmounted from the DOM",
    example: {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId: "element-123"
    },
    fields: {
      timestamp: {
        type: "date",
        required: true,
        description: "When the element was unmounted"
      },
      elementId: {
        type: "string",
        required: true,
        description: "The unique ID of the element instance"
      }
    }
  },
  "error": {
    type: "object",
    description: "Fired when the element encounters an error",
    example: {
      errorMessage: "Failed to load resource",
      errorCode: "RESOURCE_LOAD_FAILED",
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId: "element-123"
    },
    fields: {
      errorMessage: {
        type: "string",
        required: true,
        description: "Error message"
      },
      errorCode: {
        type: "string",
        required: false,
        description: "Error code if available"
      },
      timestamp: {
        type: "date",
        required: true,
        description: "When the error occurred"
      },
      elementId: {
        type: "string",
        required: true,
        description: "The unique ID of the element instance"
      }
    }
  },
  "ready": {
    type: "object",
    description: "Fired when the element is fully initialized and ready for interaction",
    example: {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId: "element-123"
    },
    fields: {
      timestamp: {
        type: "date",
        required: true,
        description: "When the element became ready"
      },
      elementId: {
        type: "string",
        required: true,
        description: "The unique ID of the element instance"
      }
    }
  },
  "stateChanged": {
    type: "object",
    description: "Fired when the element's internal state changes",
    example: {
      previousState: "idle",
      currentState: "loading",
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      elementId: "element-123"
    },
    fields: {
      previousState: {
        type: "string",
        required: false,
        description: "Previous state of the element"
      },
      currentState: {
        type: "string",
        required: true,
        description: "Current state of the element"
      },
      timestamp: {
        type: "date",
        required: true,
        description: "When the state changed"
      },
      elementId: {
        type: "string",
        required: true,
        description: "The unique ID of the element instance"
      }
    }
  }
};
function mergeWithBaseEvents(customEvents) {
  return {
    ...baseElementEvents,
    ...customEvents
  };
}
__name(mergeWithBaseEvents, "mergeWithBaseEvents");
function createEventNameGetter(elementType) {
  return (eventName) => `${elementType}:${eventName}`;
}
__name(createEventNameGetter, "createEventNameGetter");

// src/index.ts
var index_default = factory_default;

export { ElementInstance, IframeContentResizeManager, ParentResizeManager, SmarterElement, SmarterElements, baseElementEvents, broadcastElementEvent, createElementEventBroadcaster, createElementEvents, createEventNameGetter, createJWT, factory_default as createSmarterElements, debounce, decodeJWT, index_default as default, getElementEventSchemas, getJWTValue, listen, mergeWithBaseEvents, registerElementEventSchemas, setupUnifiedContentResize, setupUnifiedParentResize, throttle, useElement, useElementEvents, useSmarterElements };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map